import { Calendar, ArrowLeft, User, Clock, Tag } from "lucide-react";
import { motion } from "motion/react";
import { Link, useParams } from "react-router";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { blogPosts, getBlogPost, type BlogBlock } from "../data/blogs";

function BlockRenderer({ block }: { block: BlogBlock }) {
  if (block.type === "paragraph") {
    return <p className="text-lg leading-relaxed text-muted-foreground">{block.text}</p>;
  }

  if (block.type === "heading") {
    return <h2 className="text-3xl md:text-4xl mt-10 mb-4">{block.text}</h2>;
  }

  if (block.type === "list") {
    return (
      <ul className="space-y-3 pl-6 list-disc text-lg leading-relaxed text-muted-foreground">
        {block.items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    );
  }

  if (block.type === "quote") {
    return (
      <figure className="rounded-3xl border border-border bg-primary/5 p-8">
        <blockquote className="text-2xl leading-relaxed text-foreground italic">
          {block.text}
        </blockquote>
        {block.author ? <figcaption className="mt-4 text-sm uppercase tracking-[0.2em] text-muted-foreground">{block.author}</figcaption> : null}
      </figure>
    );
  }

  if (block.type === "image") {
    return (
      <figure className="space-y-3">
        <div className="overflow-hidden rounded-3xl border border-border shadow-sm">
          <img src={block.src} alt={block.alt} className="h-[360px] w-full object-cover" />
        </div>
        {block.caption ? <figcaption className="text-sm text-muted-foreground">{block.caption}</figcaption> : null}
      </figure>
    );
  }

  const isDirectVideoFile = /\.(mp4|webm|ogg)(\?.*)?$/i.test(block.embedUrl);

  if (isDirectVideoFile) {
    return (
      <figure className="space-y-3">
        <div className="overflow-hidden rounded-3xl border border-border shadow-sm bg-black">
          <video
            className="aspect-video w-full"
            controls
            preload="metadata"
            playsInline
          >
            <source src={block.embedUrl} />
            Your browser does not support the video tag.
          </video>
        </div>
        <div>
          <p className="font-medium">{block.title}</p>
          {block.caption ? <p className="text-sm text-muted-foreground mt-1">{block.caption}</p> : null}
        </div>
      </figure>
    );
  }

  return (
    <figure className="space-y-3">
      <div className="overflow-hidden rounded-3xl border border-border shadow-sm bg-black">
        <iframe
          title={block.title}
          src={block.embedUrl}
          className="aspect-video w-full"
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
      <div>
        <p className="font-medium">{block.title}</p>
        {block.caption ? <p className="text-sm text-muted-foreground mt-1">{block.caption}</p> : null}
      </div>
    </figure>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const post = slug ? getBlogPost(slug) : undefined;

  if (!post) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="text-center max-w-xl">
          <h1 className="text-4xl mb-4">Blog post not found</h1>
          <p className="text-muted-foreground mb-6">The article you are looking for may have been moved or removed.</p>
          <Link to="/news">
            <Button>Back to News</Button>
          </Link>
        </div>
      </div>
    );
  }

  const relatedPosts = blogPosts.filter((entry) => entry.slug !== post.slug).slice(0, 2);

  return (
    <main className="bg-white text-foreground">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={post.coverImage} alt={post.title} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/45 to-black/20" />
        </div>

        <div className="relative z-10 pb-20 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link to="/news">
              <Button variant="ghost" size="sm" className="mb-8 text-white hover:bg-white/10">
                <ArrowLeft className="mr-2" size={18} />
                Back to News
              </Button>
            </Link>

            <div className="max-w-4xl space-y-6">
              <Badge className="w-fit bg-white/15 text-white border-white/20 backdrop-blur-sm">
                {post.category}
              </Badge>
              <h1 className="text-4xl md:text-6xl leading-tight font-bold">{post.title}</h1>
              <p className="text-xl md:text-2xl leading-relaxed text-white/90">{post.excerpt}</p>

              <div className="flex flex-wrap items-center gap-6 text-white/90">
                <div className="flex items-center gap-2">
                  <User size={18} />
                  <span>{post.author}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar size={18} />
                  <span>
                    {new Date(post.date).toLocaleDateString("en-US", {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock size={18} />
                  <span>{post.readTime}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_0.45fr] gap-12 items-start">
            <motion.article
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="space-y-8"
            >
              {post.blocks.map((block, index) => (
                <BlockRenderer key={`${block.type}-${index}`} block={block} />
              ))}
            </motion.article>

            <aside className="sticky top-28 space-y-6">
              <div className="rounded-3xl border border-border bg-muted/20 p-8 space-y-6">
                <h2 className="text-2xl">About this post</h2>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Tag size={16} />
                  <span>{post.category}</span>
                </div>
                <div className="space-y-3">
                  {post.highlights.map((item) => (
                    <div key={item} className="rounded-2xl bg-white border border-border p-4 text-sm leading-relaxed">
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              {/* <div className="rounded-3xl border border-border bg-primary text-primary-foreground p-8">
                <h3 className="text-2xl mb-3">More stories</h3>
                <p className="text-primary-foreground/85 mb-6">
                  Explore more articles from the Immerse Foundation blog.
                </p>
                <div className="space-y-3">
                  {relatedPosts.map((relatedPost) => (
                    <Link key={relatedPost.slug} to={`/news/${relatedPost.slug}`} className="block rounded-2xl bg-white/10 p-4 hover:bg-white/15 transition-colors">
                      <p className="font-semibold leading-snug">{relatedPost.title}</p>
                      <p className="text-sm text-primary-foreground/75 mt-1">{relatedPost.readTime}</p>
                    </Link>
                  ))}
                </div>
              </div> */}
            </aside>
          </div>
        </div>
      </section>
    </main>
  );
}